/**
 * Floor Plan Detail Header DOM Component
 *
 * Creates the sticky header element for the floor plan detail sheet, containing the title,
 * details (beds, baths, sqft), and a back button.
 *
 * @module nativeInteractiveSiteMap/components/floorPlanDetailHeader.dom
 */

import fastdom from 'fastdom';
import { backButtonIconSvg } from 'constants/svg-assets';

/**
 * Renders detail items (beds, baths, sqft)
 * @param {Object} details - Object with beds, baths, sqft
 * @returns {string} HTML string for detail items
 */
function renderDetails(details = {}) {
  const items = [];
  
  if (details.bedrooms) {
    items.push(`${details.bedrooms} Bed${details.bedrooms !== 1 ? 's' : ''}`);
  }
  
  if (details.bathrooms) {
    items.push(`${details.bathrooms} Bath${details.bathrooms !== 1 ? 's' : ''}`);
  }
  
  if (details.squareFeet) {
    items.push(`${details.squareFeet} Sq Ft`);
  }
  
  return items.map(item => `<span>${item}</span>`).join('');
}

/**
 * Renders the floor plan detail header
 * @param {string} title - Floor plan name
 * @param {Object} details - Object with beds, baths, sqft
 * @returns {string} HTML string for the header
 */
export function renderFloorPlanDetailHeader(title = '', details = {}) {
  const detailsHtml = renderDetails(details);
  
  return `
    <div class="nhs-c-native-interactive-site-map__floorplan-detail-header nhs-mq-native-interactive-site-map__floorplan-detail-header-large" 
      data-floorplan-detail-header>
      <div class="nhs-u-display--flex nhs-u-items-center nhs-u-height--full">
        <button class="nhs-c-native-interactive-site-map__back-button" aria-label="Go back" data-back-button>
          ${backButtonIconSvg}
        </button>
      </div>
      <div class="nhs-c-native-interactive-site-map__header-main nhs-u-grow--1">
        <h4 data-floorplan-detail-title 
          class="nhs-c-native-interactive-site-map__panel-title nhs-mq-native-interactive-site-map__panel-title-medium nhs-o-typography--sans-body-1-medium-medium"
            data-qa="native_ism_floorplan_detail_title">${title}
        </h4>
        <div 
          class="nhs-c-native-interactive-site-map__floorplan-details nhs-mq-native-interactive-site-map__floorplan-details-large nhs-o-typography--sans-label-1-extra-extra-small-regular"
          data-detail-items>
          ${detailsHtml}
        </div>
      </div>
      <span class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--floorplan nhs-u-shrink--0" data-detail-badge>
        Floor plan
      </span>
    </div>
  `;
}

export default function createFloorPlanDetailHeader() {
  let headerElement = null;
  let backButton = null;
  let titleElement = null;
  let detailsContainer = null;
  let backButtonClickHandler = null;

  function initializeElements(html) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    headerElement = wrapper.querySelector('[data-floorplan-detail-header]');
    backButton = headerElement.querySelector('[data-back-button]');
    titleElement = headerElement.querySelector('[data-floorplan-detail-title]');
    detailsContainer = headerElement.querySelector('[data-detail-items]');
  }

  return {
    getElement() {
      if (!headerElement) {
        initializeElements(renderFloorPlanDetailHeader('', {}));
      }
      return headerElement;
    },
    
    setTitle(text) {
      if (!titleElement) {
        this.getElement();
      }
      fastdom.mutate(() => {
        titleElement.textContent = text;
      });
    },
    
    setDetails(details) {
      if (!detailsContainer) {
        this.getElement();
      }
      fastdom.mutate(() => {
        detailsContainer.innerHTML = renderDetails(details);
      });
    },

    onBackClick(callback) {
      if (!backButton) {
        this.getElement();
      }
      backButtonClickHandler = callback;
      backButton.addEventListener('click', callback);
    },

    hide() {
      fastdom.mutate(() => {
        this.getElement().hidden = true;
      });
    },

    show() {
      fastdom.mutate(() => {
        this.getElement().hidden = false;
      });
    },

    remove() {
      if (backButton && backButtonClickHandler) {
        backButton.removeEventListener('click', backButtonClickHandler);
        backButtonClickHandler = null;
      }
      fastdom.mutate(() => {
        headerElement?.remove();
      });
    },
  };
}
